import TemplatePreview from '@/components/TemplatePreview';
import { useToast } from '@/hooks/useToast';
import { CreateTemplateSchema } from '@/schemas/template';
import { mediaService } from '@/services/mediaService';
import { templatesService } from '@/services/templatesService';
import { whatsappConfigService } from '@/services/whatsappConfigService';
import {
  BUTTON_TYPES,
  DEFAULT_LANGUAGE,
  HEADER_FORMATS,
  LANGUAGE_OPTIONS,
  TEMPLATE_CATEGORIES,
  VALIDATION_LIMITS,
} from '@/types/templates';
import {
  createErrorHandler,
  handleTemplateOperation,
} from '@/utils/templateErrors';
import {
  detectDynamicVariables,
  validatePhoneNumber,
  validateTemplateName,
  validateUrl,
} from '@/utils/templateValidation';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Template creation form with comprehensive field support
 * @returns {JSX.Element} TemplateCreate component
 */
const TemplateCreate = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch WhatsApp configuration for preview
  const { data: whatsappConfigData } = useQuery({
    queryKey: ['whatsapp-config', projectId],
    queryFn: () => whatsappConfigService.getWhatsAppConfig(projectId),
    enabled: !!projectId,
  });

  const whatsappConfig = whatsappConfigData?.data?.whatsappConfig;

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateTemplateSchema),
    defaultValues: {
      projectId: projectId,
      name: '',
      category: '',
      language: DEFAULT_LANGUAGE,
      header: {
        format: undefined,
        text: undefined,
        header_text: undefined,
        url: undefined,
        key: undefined,
      },
      body: {
        text: '',
        body_text: undefined,
      },
      footer: {
        text: undefined,
      },
      buttons: undefined,
    },
    mode: 'onChange',
  });

  const {
    fields: buttonFields,
    append: appendButton,
    remove: removeButton,
  } = useFieldArray({
    control,
    name: 'buttons',
  });

  // Watch form values
  const watchedCategory = watch('category');
  const watchedHeaderFormat = watch('header.format');
  const watchedHeaderText = watch('header.text');
  const watchedBodyText = watch('body.text');
  const watchedFooterText = watch('footer.text');
  const watchedHeaderFile = watch('header.attachment.file');

  // Create a virtual template for preview
  const virtualTemplate = useMemo(() => {
    const headerParams = watch('header.header_text') || [];
    const bodyParams = watch('body.body_text') || [];
    const buttonParams = {};

    buttonFields.forEach((field, index) => {
      const type = watch(`buttons.${index}.type`);
      if (type === 'copy_code') {
        buttonParams[`copy_code_${index}`] = watch(`buttons.${index}.example`);
      }
      if (type === 'url') {
        const url = watch(`buttons.${index}.url`);
        if (url && url.includes('{{')) {
          buttonParams[`url_${index}`] = watch(`buttons.${index}.example`);
        }
      }
    });

    return {
      category: watchedCategory,
      header: {
        format: watchedHeaderFormat,
        text: watchedHeaderText,
      },
      body: {
        text: watchedBodyText,
      },
      footer: {
        text: watchedFooterText,
      },
      buttons: buttonFields.map((field, index) => ({
        type: watch(`buttons.${index}.type`),
        text: watch(`buttons.${index}.text`),
        url: watch(`buttons.${index}.url`),
        phoneNumber: watch(`buttons.${index}.phoneNumber`),
      })),
      headerParams,
      bodyParams,
      buttonParams,
    };
  }, [
    watchedCategory,
    watchedHeaderFormat,
    watchedHeaderText,
    watchedBodyText,
    watchedFooterText,
    buttonFields,
    watch,
  ]);

  const headerFilePreview = useMemo(() => {
    if (watchedHeaderFile && watchedHeaderFile[0]) {
      return watchedHeaderFile[0];
    }
    return null;
  }, [watchedHeaderFile]);

  // Initialize body_text array when variables are detected
  useEffect(() => {
    if (hasVariables(watchedBodyText)) {
      const bodyVariables = getUniqueVariables(watchedBodyText);
      const sortedVariables = bodyVariables.sort(
        (a, b) => parseInt(a) - parseInt(b)
      );
      const currentBodyText = getValues('body.body_text') || [];

      // Ensure the array has the right length
      if (currentBodyText.length !== sortedVariables.length) {
        const newBodyText = new Array(sortedVariables.length).fill('');
        // Preserve existing values if they exist
        currentBodyText.forEach((value, index) => {
          if (index < newBodyText.length) {
            newBodyText[index] = value;
          }
        });
        setValue('body.body_text', newBodyText);
      }
    }
  }, [watchedBodyText, setValue, getValues]);

  // Initialize header_text array when variables are detected
  useEffect(() => {
    if (
      hasVariables(watchedHeaderText) &&
      validateHeaderVariables(watchedHeaderText)
    ) {
      const currentHeaderText = getValues('header.header_text') || [];

      // Ensure the array has exactly one element for header (single variable)
      if (currentHeaderText.length !== 1) {
        const newHeaderText = [currentHeaderText[0] || ''];
        setValue('header.header_text', newHeaderText);
      }
    }
  }, [watchedHeaderText, setValue, getValues]);

  // Create template mutation with error handling
  const createTemplateMutation = useMutation({
    mutationFn: async templateData => {
      return await handleTemplateOperation(
        () => templatesService.createTemplate(projectId, templateData),
        { operation: 'createTemplate', templateData }
      );
    },
    onSuccess: response => {
      showSuccess('Template created successfully');
      queryClient.invalidateQueries(['templates', projectId]);
      queryClient.invalidateQueries(['template-stats', projectId]);
      navigate(`/projects/${projectId}/templates/${response.data._id}`);
    },
    onError: error => {
      const errorHandler = createErrorHandler('createTemplate');
      const templateError = errorHandler(error);
      showError(templateError.message || 'Failed to create template');
    },
  });

  // Handle form submission
  const onSubmit = async data => {
    setIsSubmitting(true);
    try {
      // Process dynamic variables for body_text array
      const processedData = { ...data };

      // Process header - remove if format is not selected
      if (
        !data.header?.format ||
        data.header.format === '' ||
        data.header.format === undefined
      ) {
        delete processedData.header;
      } else {
        if (data.header.format !== 'TEXT') {
          delete processedData.header.text;
          delete processedData.header.header_text;
        }
      }

      // Process body variables (multiple variables)
      if (hasVariables(data.body.text)) {
        const bodyVariables = getUniqueVariables(data.body.text);

        // Sort variables numerically to maintain order (1, 2, 3, etc.)
        const sortedVariables = bodyVariables.sort(
          (a, b) => parseInt(a) - parseInt(b)
        );

        // Get values from body_text array in the correct order
        const bodyTextArray = sortedVariables
          .map(variable => {
            const index = parseInt(variable) - 1; // Convert to 0-based index
            return data.body.body_text && data.body.body_text[index]
              ? data.body.body_text[index]
              : '';
          })
          .filter(value => value.trim() !== ''); // Remove empty values

        processedData.body.body_text = bodyTextArray;
      }

      // Process buttons - clean up based on button type
      if (processedData.buttons) {
        processedData.buttons = processedData.buttons.map(button => {
          const cleanedButton = { type: button.type };

          // Add fields based on button type
          if (button.type === 'url') {
            if (button.text) cleanedButton.text = button.text;
            if (button.url) cleanedButton.url = button.url;
            // Only add example if URL has dynamic variables
            if (button.url && hasVariables(button.url) && button.example) {
              cleanedButton.example = button.example;
            }
          } else if (button.type === 'phone_number') {
            if (button.text) cleanedButton.text = button.text;
            if (button.phoneNumber)
              cleanedButton.phoneNumber = button.phoneNumber;
          } else if (button.type === 'copy_code') {
            if (button.example) cleanedButton.example = button.example;
          } else if (button.type === 'quick_reply') {
            if (button.text) cleanedButton.text = button.text;
          }

          return cleanedButton;
        });
      }

      // Process footer - remove if text is empty
      if (!data.footer?.text || data.footer.text.trim() === '') {
        delete processedData.footer;
      }

      // Transform and submit the data
      await createTemplateMutation.mutateAsync(processedData);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form errors
  const onFormError = validationErrors => {
    console.group('Form Validation Errors');
    console.error(validationErrors);
    console.groupEnd();

    if (validationErrors.root) {
      showError(validationErrors.root.message || 'Form validation failed');
    } else if (validationErrors['']) {
      showError(validationErrors[''].message || 'Form validation failed');
    } else {
      // Create a more specific error message based on the fields
      const errorFields = Object.keys(validationErrors)
        .map(field => field.charAt(0).toUpperCase() + field.slice(1))
        .join(', ');

      showError(
        `Please fix validation errors${errorFields ? ` in: ${errorFields}` : ''}`
      );
    }
  };

  // Helper functions for preview
  const hasVariables = text => {
    return text && /\{\{\d+\}\}/.test(text);
  };

  const getUniqueVariables = text => {
    if (!text) return [];
    const matches = text.match(/\{\{\d+\}\}/g);
    if (!matches) return [];
    return [...new Set(matches.map(m => m.replace(/[{}]/g, '')))];
  };

  // Helper function to validate header text (max 1 variable)
  const validateHeaderVariables = text => {
    const variables = detectDynamicVariables(text);
    return variables.length <= 1;
  };

  // Add button
  const addButton = () => {
    if (buttonFields.length < VALIDATION_LIMITS.MAX_BUTTONS_PER_TEMPLATE) {
      appendButton({
        type: BUTTON_TYPES.QUICK_REPLY,
        example: '',
        url: '',
        phoneNumber: '',
      });
    }
  };

  // Remove button
  const removeButtonHandler = index => {
    removeButton(index);
  };

  // Handle file upload
  const handleFileUpload = async e => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      // Clear previous error if any
      // clearErrors('header.attachment.file');

      const uploadResponse = await mediaService.uploadFileOnFacebook(
        projectId,
        file
      );

      if (uploadResponse.data && uploadResponse.data.handle) {
        // Set the handle as the URL value
        setValue('header.url', uploadResponse.data.handle, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue('header.key', uploadResponse.data.key, {
          shouldValidate: true,
          shouldDirty: true,
        });
        showSuccess('File uploaded successfully');
      } else {
        throw new Error('No handle received from media upload');
      }
    } catch (error) {
      console.error('File upload failed:', error);
      showError('Failed to upload media file');
      // Reset file input if needed or show error on field
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 p-6 border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Create Template
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Create a new WhatsApp message template
            </p>
          </div>
          <button
            onClick={() => navigate(`/projects/${projectId}/templates`)}
            className="text-gray-600 hover:text-gray-900"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex">
        {/* Form Container - 75% width */}
        <div className="flex-1">
          <div className="p-6">
            <form
              onSubmit={handleSubmit(onSubmit, onFormError)}
              className="space-y-8"
            >
              {/* Basic Information Section */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Basic Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Template Name */}
                  <div className="md:col-span-1">
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Template Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      {...register('name', {
                        required: 'Template name is required',
                        maxLength: {
                          value: VALIDATION_LIMITS.NAME_MAX_LENGTH,
                          message: `Name must be less than ${VALIDATION_LIMITS.NAME_MAX_LENGTH} characters`,
                        },
                        validate: value => {
                          const nameErrors = validateTemplateName(value);
                          return nameErrors.length === 0 || nameErrors[0];
                        },
                      })}
                      onChange={e => {
                        // Transform input: replace spaces with underscores and convert to lowercase
                        const transformedValue = e.target.value
                          .replace(/\s+/g, '_') // Replace spaces with underscores
                          .toLowerCase() // Convert to lowercase
                          .replace(/[^a-z0-9_]/g, ''); // Remove any invalid characters

                        // Update the form value
                        setValue('name', transformedValue, {
                          shouldValidate: true,
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter template name (spaces will become underscores)"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.name.message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Spaces will automatically become underscores. Only
                      lowercase letters, numbers, and underscores allowed.
                    </p>
                  </div>

                  {/* Category */}
                  <div className="md:col-span-1">
                    <label
                      htmlFor="category"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Template Category *
                    </label>
                    <select
                      id="category"
                      {...register('category', {
                        required: 'Category is required',
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select category</option>
                      <option value={TEMPLATE_CATEGORIES.MARKETING}>
                        Marketing
                      </option>
                      <option value={TEMPLATE_CATEGORIES.UTILITY}>
                        Utility
                      </option>
                      <option value={TEMPLATE_CATEGORIES.AUTHENTICATION}>
                        Authentication
                      </option>
                    </select>
                    {errors.category && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.category.message}
                      </p>
                    )}
                  </div>

                  {/* Language */}
                  <div className="md:col-span-1">
                    <label
                      htmlFor="language"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Template Language *
                    </label>
                    <select
                      id="language"
                      {...register('language', {
                        required: 'Language is required',
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="">Select language</option>
                      {LANGUAGE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.language && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.language.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Header Section - Hidden for Authentication */}
              {watchedCategory !== TEMPLATE_CATEGORIES.AUTHENTICATION && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Header Section
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Optional header section for your template
                  </p>

                  <div className="space-y-6">
                    {/* Format Selection */}
                    <div>
                      <label
                        htmlFor="header.format"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Format
                      </label>
                      <select
                        id="header.format"
                        {...register('header.format')}
                        onChange={e => {
                          const value =
                            e.target.value === '' ? undefined : e.target.value;
                          setValue('header.format', value, {
                            shouldValidate: true,
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        <option value="">Select format</option>
                        <option value={HEADER_FORMATS.TEXT}>Text</option>
                        <option value={HEADER_FORMATS.IMAGE}>Image</option>
                        <option value={HEADER_FORMATS.VIDEO}>Video</option>
                        <option value={HEADER_FORMATS.DOCUMENT}>
                          Document
                        </option>
                      </select>
                    </div>

                    {/* Text Format Fields */}
                    {watchedHeaderFormat === HEADER_FORMATS.TEXT && (
                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="header.text"
                            className="block text-sm font-medium text-gray-700 mb-1"
                          >
                            Text *
                          </label>
                          <textarea
                            id="header.text"
                            {...register('header.text', {
                              required:
                                watchedHeaderFormat === HEADER_FORMATS.TEXT
                                  ? 'Header text is required'
                                  : false,
                              validate: value => {
                                if (value && !validateHeaderVariables(value)) {
                                  return 'Header text can have maximum 1 dynamic variable ({{1}})';
                                }
                                return true;
                              },
                            })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="Enter header text (max 1 dynamic variable: {{1}})"
                          />
                          {errors.header?.text && (
                            <p className="mt-1 text-sm text-red-600">
                              {errors.header.text.message}
                            </p>
                          )}
                          {watchedHeaderText &&
                            !validateHeaderVariables(watchedHeaderText) && (
                              <p className="mt-1 text-sm text-red-600">
                                Header text can have maximum 1 dynamic variable
                                ({'{{1}}'})
                              </p>
                            )}
                        </div>

                        {/* Example Values for Variables */}
                        {hasVariables(watchedHeaderText) &&
                          validateHeaderVariables(watchedHeaderText) && (
                            <div>
                              <label
                                htmlFor="header.header_text.0"
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Example Value for {'{{1}}'}
                              </label>
                              <input
                                type="text"
                                id="header.header_text.0"
                                {...register('header.header_text.0', {
                                  required: hasVariables(watchedHeaderText)
                                    ? 'Example value is required when using dynamic variables'
                                    : false,
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Enter example value for {{1}} (e.g., John Doe)`}
                              />
                              {errors.header?.header_text?.[0] && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.header.header_text[0].message}
                                </p>
                              )}
                              <p className="mt-1 text-xs text-gray-500">
                                This example will be used to preview how the
                                header will appear
                              </p>
                            </div>
                          )}
                      </div>
                    )}

                    {/* Media Format Fields */}
                    {watchedHeaderFormat &&
                      watchedHeaderFormat !== HEADER_FORMATS.TEXT && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Attachment
                            </label>
                            <div className="space-y-4">
                              <div>
                                <input
                                  type="file"
                                  accept={
                                    watchedHeaderFormat === HEADER_FORMATS.IMAGE
                                      ? 'image/*'
                                      : watchedHeaderFormat ===
                                          HEADER_FORMATS.VIDEO
                                        ? 'video/*'
                                        : watchedHeaderFormat ===
                                            HEADER_FORMATS.DOCUMENT
                                          ? '.pdf,.doc,.docx,.txt'
                                          : '*'
                                  }
                                  {...register('header.attachment.file')}
                                  onChange={e => {
                                    // Call react-hook-form's onChange
                                    register('header.attachment.file').onChange(
                                      e
                                    );
                                    // Call our custom upload handler
                                    handleFileUpload(e);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  disabled={isUploading}
                                />
                                {isUploading && (
                                  <p className="mt-1 text-sm text-blue-600">
                                    Uploading file...
                                  </p>
                                )}
                                {errors.header?.attachment?.file && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.header.attachment.file.message}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Body Section */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Body Section
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {watchedCategory === TEMPLATE_CATEGORIES.AUTHENTICATION
                    ? 'Required body section for authentication template'
                    : 'Required body section for your template'}
                </p>

                <div className="space-y-6">
                  {/* Body Text - Hidden for Authentication */}
                  {watchedCategory !== TEMPLATE_CATEGORIES.AUTHENTICATION && (
                    <div>
                      <label
                        htmlFor="body.text"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Text *
                      </label>
                      <textarea
                        id="body.text"
                        {...register('body.text', {
                          required:
                            watchedCategory !==
                            TEMPLATE_CATEGORIES.AUTHENTICATION
                              ? 'Body text is required'
                              : false,
                        })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Enter body text"
                      />
                      {errors.body?.text && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.body.text.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Example Values for Variables */}
                  {hasVariables(watchedBodyText) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Example Values for Dynamic Variables
                      </label>
                      <div className="space-y-3">
                        {getUniqueVariables(watchedBodyText)
                          .sort((a, b) => parseInt(a) - parseInt(b))
                          .map((variable, index) => (
                            <div key={variable}>
                              <label
                                htmlFor={`body.body_text.${index}`}
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Example for {`{{${variable}}}`}
                              </label>
                              <input
                                type="text"
                                id={`body.body_text.${index}`}
                                {...register(`body.body_text.${index}`, {
                                  required: `Example value is required for {{${variable}}}`,
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder={`Enter example value for {{${variable}}} (e.g., ${variable === '1' ? 'John Doe' : variable === '2' ? '12345' : 'Example Value'})`}
                              />
                              {errors.body?.body_text?.[index] && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.body.body_text[index].message}
                                </p>
                              )}
                            </div>
                          ))}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Variables found:{' '}
                        {getUniqueVariables(watchedBodyText)
                          .sort((a, b) => parseInt(a) - parseInt(b))
                          .map(v => `{{${v}}}`)
                          .join(', ')}
                      </p>
                      <div className="mt-2 p-2 bg-blue-50 rounded-md">
                        <p className="text-xs font-medium text-blue-800 mb-1">
                          Values that will be added to body_text array:
                        </p>
                        <div className="text-xs text-blue-700">
                          {getUniqueVariables(watchedBodyText)
                            .sort((a, b) => parseInt(a) - parseInt(b))
                            .map((variable, index) => {
                              const value = watch(`body.body_text.${index}`);
                              return (
                                <span key={variable}>
                                  [{index}]:{' '}
                                  {value || `{{${variable}}} (not set)`}
                                  {index <
                                    getUniqueVariables(watchedBodyText).length -
                                      1 && ', '}
                                </span>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Authentication-specific fields */}
                  {watchedCategory === TEMPLATE_CATEGORIES.AUTHENTICATION && (
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="body.securityRecommendation"
                          {...register('body.securityRecommendation')}
                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor="body.securityRecommendation"
                          className="ml-2 block text-sm text-gray-900"
                        >
                          Add security recommendation
                        </label>
                      </div>

                      <div>
                        <label
                          htmlFor="body.codeExpirationMinutes"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Code Expiration Minutes
                        </label>
                        <input
                          type="number"
                          id="body.codeExpirationMinutes"
                          {...register('body.codeExpirationMinutes', {
                            min: {
                              value: 1,
                              message: 'Must be at least 1 minute',
                            },
                            max: {
                              value: 1440,
                              message:
                                'Must be less than 1440 minutes (24 hours)',
                            },
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter expiration time in minutes"
                        />
                        {errors.body?.codeExpirationMinutes && (
                          <p className="mt-1 text-sm text-red-600">
                            {errors.body.codeExpirationMinutes.message}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Section - Hidden for Authentication */}
              {watchedCategory !== TEMPLATE_CATEGORIES.AUTHENTICATION && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Footer Section
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Optional footer section for your template
                  </p>

                  <div>
                    <label
                      htmlFor="footer.text"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Text
                    </label>
                    <textarea
                      id="footer.text"
                      {...register('footer.text')}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Enter footer text"
                    />
                  </div>
                </div>
              )}

              {/* Buttons Section - Hidden for Authentication */}
              {watchedCategory !== TEMPLATE_CATEGORIES.AUTHENTICATION && (
                <div className="bg-white shadow rounded-lg p-6">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        Buttons Section
                      </h3>
                      <p className="text-sm text-gray-600">
                        Optional buttons for your template (up to{' '}
                        {VALIDATION_LIMITS.MAX_BUTTONS_PER_TEMPLATE})
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={addButton}
                      disabled={
                        buttonFields.length >=
                        VALIDATION_LIMITS.MAX_BUTTONS_PER_TEMPLATE
                      }
                      className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Button
                    </button>
                  </div>

                  {buttonFields.length === 0 ? (
                    <div className="text-center py-8">
                      <svg
                        className="mx-auto h-12 w-12 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
                        />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">
                        No buttons added
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Add buttons to provide interactive elements for your
                        template.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {buttonFields.map((field, index) => (
                        <div
                          key={field.id}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-sm font-medium text-gray-900">
                              Button {index + 1}
                            </h4>
                            <button
                              type="button"
                              onClick={() => removeButtonHandler(index)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <svg
                                className="h-5 w-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label
                                htmlFor={`buttons.${index}.type`}
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Type *
                              </label>
                              <select
                                id={`buttons.${index}.type`}
                                {...register(`buttons.${index}.type`, {
                                  required: 'Button type is required',
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              >
                                <option value="">Select type</option>
                                <option value={BUTTON_TYPES.COPY_CODE}>
                                  Copy Code
                                </option>
                                <option value={BUTTON_TYPES.PHONE_NUMBER}>
                                  Phone Number
                                </option>
                                <option value={BUTTON_TYPES.QUICK_REPLY}>
                                  Quick Reply
                                </option>
                                <option value={BUTTON_TYPES.URL}>URL</option>
                              </select>
                              {errors.buttons?.[index]?.type && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.buttons[index].type.message}
                                </p>
                              )}
                            </div>

                            {/* Button Text Field - shown for url, phone_number, and quick_reply buttons */}
                            {['url', 'phone_number', 'quick_reply'].includes(
                              watch(`buttons.${index}.type`)
                            ) && (
                              <div>
                                <label
                                  htmlFor={`buttons.${index}.text`}
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  Button Text *
                                </label>
                                <input
                                  type="text"
                                  id={`buttons.${index}.text`}
                                  {...register(`buttons.${index}.text`, {
                                    required: 'Button text is required',
                                    maxLength: {
                                      value:
                                        VALIDATION_LIMITS.BUTTON_TEXT_MAX_LENGTH,
                                      message: `Button text must be less than ${VALIDATION_LIMITS.BUTTON_TEXT_MAX_LENGTH} characters`,
                                    },
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  placeholder="Enter button text"
                                />
                                {errors.buttons?.[index]?.text && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.buttons[index].text.message}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Example Field - shown for copy_code buttons */}
                            {watch(`buttons.${index}.type`) === 'copy_code' && (
                              <div>
                                <label
                                  htmlFor={`buttons.${index}.example`}
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  Example Code *
                                </label>
                                <input
                                  type="text"
                                  id={`buttons.${index}.example`}
                                  {...register(`buttons.${index}.example`, {
                                    required: 'Example code is required',
                                    maxLength: {
                                      value:
                                        VALIDATION_LIMITS.BUTTON_TEXT_MAX_LENGTH,
                                      message: `Example code must be less than ${VALIDATION_LIMITS.BUTTON_TEXT_MAX_LENGTH} characters`,
                                    },
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  placeholder="Enter example code (e.g., SAVE25)"
                                />
                                {errors.buttons?.[index]?.example && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.buttons[index].example.message}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Additional fields based on button type */}
                          {watch(`buttons.${index}.type`) ===
                            BUTTON_TYPES.URL && (
                            <div className="mt-4 space-y-4">
                              <div>
                                <label
                                  htmlFor={`buttons.${index}.url`}
                                  className="block text-sm font-medium text-gray-700 mb-1"
                                >
                                  URL *
                                </label>
                                <input
                                  type="url"
                                  id={`buttons.${index}.url`}
                                  {...register(`buttons.${index}.url`, {
                                    required: 'URL is required for URL buttons',
                                    validate: value => {
                                      if (value && !validateUrl(value)) {
                                        return 'Invalid URL format';
                                      }
                                      return true;
                                    },
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                  placeholder="Enter URL"
                                />
                                {errors.buttons?.[index]?.url && (
                                  <p className="mt-1 text-sm text-red-600">
                                    {errors.buttons[index].url.message}
                                  </p>
                                )}
                              </div>

                              {/* URL Example Field - only shown when URL contains dynamic variables */}
                              {watch(`buttons.${index}.url`) &&
                                hasVariables(watch(`buttons.${index}.url`)) && (
                                  <div>
                                    <label
                                      htmlFor={`buttons.${index}.example`}
                                      className="block text-sm font-medium text-gray-700 mb-1"
                                    >
                                      URL Example *
                                    </label>
                                    <input
                                      type="url"
                                      id={`buttons.${index}.example`}
                                      {...register(`buttons.${index}.example`, {
                                        required:
                                          'URL example is required when URL contains dynamic variables',
                                        validate: value => {
                                          if (value && !validateUrl(value)) {
                                            return 'Invalid URL format';
                                          }
                                          return true;
                                        },
                                      })}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                      placeholder="Enter example URL (e.g., https://example.com/offer/EXCLUSIVE123)"
                                    />
                                    {errors.buttons?.[index]?.example && (
                                      <p className="mt-1 text-sm text-red-600">
                                        {errors.buttons[index].example.message}
                                      </p>
                                    )}
                                  </div>
                                )}
                            </div>
                          )}

                          {watch(`buttons.${index}.type`) ===
                            BUTTON_TYPES.PHONE_NUMBER && (
                            <div className="mt-4">
                              <label
                                htmlFor={`buttons.${index}.phoneNumber`}
                                className="block text-sm font-medium text-gray-700 mb-1"
                              >
                                Phone Number *
                              </label>
                              <input
                                type="tel"
                                id={`buttons.${index}.phoneNumber`}
                                {...register(`buttons.${index}.phoneNumber`, {
                                  required:
                                    'Phone number is required for phone number buttons',
                                  validate: value => {
                                    if (value && !validatePhoneNumber(value)) {
                                      return 'Invalid phone number format. Use international format (e.g., +1234567890)';
                                    }
                                    return true;
                                  },
                                })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                placeholder="Enter phone number (e.g., +1234567890)"
                              />
                              {errors.buttons?.[index]?.phoneNumber && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.buttons[index].phoneNumber.message}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end pb-8">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Creating Template...' : 'Create Template'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview Panel - 25% width */}
        <div className="w-1/4 bg-gray-50 border-l border-gray-200">
          <div className="p-4 sticky top-0">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Template Preview
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              How your template will appear to users
            </p>

            {/* Template Preview Section */}
            <div className="flex flex-col items-center">
              <TemplatePreview
                template={virtualTemplate}
                headerParams={virtualTemplate.headerParams}
                bodyParams={virtualTemplate.bodyParams}
                buttonParams={virtualTemplate.buttonParams}
                headerFile={headerFilePreview}
                showMobilePreview={true}
                businessName={whatsappConfig?.verifiedName}
                businessLogo={whatsappConfig?.profilePictureUrl}
                className="scale-[0.9] origin-top"
              />

              <div className="mt-8 pt-6 border-t border-gray-100 w-full">
                <div className="flex items-center space-x-2 text-xs text-gray-400 font-bold uppercase tracking-widest justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse"></div>
                  <span>Real-time Preview</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCreate;
